// ============================================================================
// Window / priority codes — single source of truth for their color identity.
//
// ESC jams two things into one "priority/window" field: arrival-time windows
// (8AM, 8-12, 10-2, 12-4, 3-7, AFTER) and special codes (TECH, LONG, PAIR,
// PART, QUOTE, REBATE, INSTAL, PLUMB, OFF). Each has a canonical color lifted
// from ESC's own palette so dispatchers keep the colors they already know.
//
// Two intensities from ONE definition (the Linear/Stripe pattern, same as
// lib/status.ts):
//   - board form  : loud fill + border — a scanning aid across many cards.
//   - dot         : a solid hue for the calm <WindowBadge> on detail pages /
//                   tables, where you're reading one record, not scanning.
//
// The Board's windowColorClasses() now delegates here, so the board's look is
// unchanged and there's exactly one place that knows "12-4 is teal".
// ============================================================================

export interface WindowColors {
  bg: string;
  border: string;
  text: string;
  /** true when the board fill is dark enough to need light text. */
  dark: boolean;
}

interface WindowDef {
  board: WindowColors;
  /** Solid hue dot for the calm WindowBadge form. */
  dot: string;
}

const DEFAULT_DEF: WindowDef = {
  // Empty / unknown code → ESC's blank-priority Pale Green.
  board: { bg: "bg-green-100", border: "border-green-300", text: "text-green-900", dark: false },
  dot: "bg-green-400",
};

const WINDOW: Record<string, WindowDef> = {
  "8AM": {
    board: { bg: "bg-fuchsia-300", border: "border-fuchsia-600", text: "text-fuchsia-900", dark: false },
    dot: "bg-fuchsia-500",
  },
  "8-12": {
    board: { bg: "bg-pink-100", border: "border-pink-300", text: "text-pink-900", dark: false },
    dot: "bg-pink-400",
  },
  "10-2": {
    board: { bg: "bg-red-300", border: "border-red-500", text: "text-red-900", dark: false },
    dot: "bg-red-500",
  },
  "12-4": {
    board: { bg: "bg-teal-200", border: "border-teal-500", text: "text-teal-900", dark: false },
    dot: "bg-teal-500",
  },
  "3-7": {
    board: { bg: "bg-blue-300", border: "border-blue-600", text: "text-blue-900", dark: false },
    dot: "bg-blue-500",
  },
  // Install arrival windows (Seth 2026-06-30): install crews run their own
  // windows, not the 8-12/12-4 service rhythm. "12-4" is shared (teal, above);
  // these two are new so they don't fall back to the pale-green default.
  "7-8": {
    board: { bg: "bg-indigo-200", border: "border-indigo-500", text: "text-indigo-900", dark: false },
    dot: "bg-indigo-500",
  },
  "11-1": {
    board: { bg: "bg-amber-200", border: "border-amber-500", text: "text-amber-900", dark: false },
    dot: "bg-amber-500",
  },
  AFTER: {
    board: { bg: "bg-lime-200", border: "border-lime-500", text: "text-lime-900", dark: false },
    dot: "bg-lime-500",
  },
  INSTAL: {
    board: { bg: "bg-green-300", border: "border-green-600", text: "text-green-900", dark: false },
    dot: "bg-green-500",
  },
  INSTALL: {
    board: { bg: "bg-green-300", border: "border-green-600", text: "text-green-900", dark: false },
    dot: "bg-green-500",
  },
  LONG: {
    board: { bg: "bg-slate-900", border: "border-black", text: "text-white", dark: true },
    dot: "bg-slate-800",
  },
  OFF: {
    board: { bg: "bg-fuchsia-200", border: "border-fuchsia-400", text: "text-fuchsia-700", dark: false },
    dot: "bg-fuchsia-400",
  },
  PAIR: {
    board: { bg: "bg-red-900", border: "border-red-950", text: "text-red-100", dark: true },
    dot: "bg-red-800",
  },
  PART: {
    board: { bg: "bg-orange-300", border: "border-orange-600", text: "text-orange-900", dark: false },
    dot: "bg-orange-500",
  },
  PLUMB: {
    board: { bg: "bg-sky-200", border: "border-sky-400", text: "text-sky-900", dark: false },
    dot: "bg-sky-500",
  },
  QUOT: {
    board: { bg: "bg-purple-500", border: "border-purple-800", text: "text-white", dark: true },
    dot: "bg-purple-500",
  },
  QUOTE: {
    board: { bg: "bg-purple-500", border: "border-purple-800", text: "text-white", dark: true },
    dot: "bg-purple-500",
  },
  REBATE: {
    board: { bg: "bg-slate-300", border: "border-slate-500", text: "text-slate-700", dark: false },
    dot: "bg-slate-400",
  },
  TECH: {
    board: { bg: "bg-yellow-300", border: "border-yellow-500", text: "text-yellow-900", dark: false },
    dot: "bg-yellow-500",
  },
};

function defFor(code: string | null | undefined): WindowDef {
  const w = (code ?? "").toUpperCase().trim();
  return WINDOW[w] ?? DEFAULT_DEF;
}

/**
 * Loud board form — fill + border + text + dark flag. This is the exact value
 * the Board has always used; windowColorClasses() in features/board/api.ts
 * now delegates here so there's one source.
 */
export function windowColors(code: string | null | undefined): WindowColors {
  return defFor(code).board;
}

/** Solid hue dot for the calm WindowBadge form (detail pages, tables). */
export function windowDot(code: string | null | undefined): string {
  return defFor(code).dot;
}
